import api from './api';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface UserData {
  id: number;
  username: string;
  email: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  role: string | number;
  isActive?: boolean;
  mustChangePassword?: boolean;
  createdAt?: string;
  lastLoginAt?: string;
  lastFiscalYearId?: number | null;
}

export interface LoginResponse {
  token: string;
  user: UserData;
  expiresAt: string;
  roles?: string[];
  permissions?: string[];
}

/**
 * Decode JWT token to get payload without verification (client-side only)
 */
function decodeJwtToken(token: string): any {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Check if JWT token is expired
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeJwtToken(token);
  if (!decoded || !decoded.exp) return true;
  const expiration = decoded.exp * 1000; // Convert to milliseconds
  return Date.now() >= expiration;
}

/**
 * Extract claims from JWT token
 */
export function getTokenClaims(token: string): any {
  return decodeJwtToken(token);
}

/**
 * Get RBAC roles from token
 */
export function getRBACRoles(token: string): string[] {
  const claims = decodeJwtToken(token);
  if (!claims) return [];
  
  const roles = claims['rbac_role'];
  if (Array.isArray(roles)) return roles;
  if (typeof roles === 'string') return [roles];
  return [];
}

/**
 * Get RBAC permissions from token
 */
export function getRBACPermissions(token: string): string[] {
  const claims = decodeJwtToken(token);
  if (!claims) return [];
  
  const permissions = claims['permission'];
  if (Array.isArray(permissions)) return permissions;
  if (typeof permissions === 'string') return [permissions];
  return [];
}

/**
 * Get user ID from token
 */
export function getUserIdFromToken(token: string): number | null {
  const claims = decodeJwtToken(token);
  if (!claims) return null;
  const id = claims['nameid'] || claims['userId'];
  return id ? parseInt(id) : null;
}

export const authService = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Store RBAC roles and permissions if available
      if (response.data.roles) {
        localStorage.setItem('rbac_roles', JSON.stringify(response.data.roles));
      }
      if (response.data.permissions) {
        localStorage.setItem('rbac_permissions', JSON.stringify(response.data.permissions));
      }
    }
    return response.data;
  },

  setLastFiscalYear: (fiscalYearId: number) =>
    api.put('/auth/me/fiscal-year', { fiscalYearId }),

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('rbac_roles');
    localStorage.removeItem('rbac_permissions');
  },

  getCurrentUser: (): UserData | null => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    // Check if token is expired
    if (isTokenExpired(token)) {
      authService.logout();
      return false;
    }
    
    return true;
  },

  getUserRoles: (): string[] => {
    const roles = localStorage.getItem('rbac_roles');
    return roles ? JSON.parse(roles) : [];
  },

  getUserPermissions: (): string[] => {
    const perms = localStorage.getItem('rbac_permissions');
    return perms ? JSON.parse(perms) : [];
  },

  hasPermission: (permission: string): boolean => {
    const perms = authService.getUserPermissions();
    const roles = authService.getUserRoles();
    
    // Super Admin Bypass
    if (roles.includes('SystemAdmin') || perms.includes('*.*')) {
      return true;
    }
    
    return perms.includes(permission);
  },

  hasAnyPermission: (...permissions: string[]): boolean => {
    const perms = authService.getUserPermissions();
    const roles = authService.getUserRoles();
    
    // Super Admin Bypass
    if (roles.includes('SystemAdmin') || perms.includes('*.*')) {
      return true;
    }
    
    return permissions.some(p => perms.includes(p));
  },

  hasRole: (roleName: string): boolean => {
    const roles = authService.getUserRoles();
    return roles.includes(roleName);
  },

  getTokenExpiry: (): Date | null => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const claims = decodeJwtToken(token);
    if (!claims || !claims.exp) return null;
    return new Date(claims.exp * 1000);
  },

  getTokenAgeMinutes: (): number | null => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const claims = decodeJwtToken(token);
    if (!claims || !claims.iat) return null;
    const issuedAt = claims.iat * 1000;
    return (Date.now() - issuedAt) / 60000;
  },
};